import { formatTimeframeLabel } from "../tradingRuntimeConfig";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getZoneQuality(zoneState) {
  if (!zoneState?.valid) {
    return 0;
  }

  const freshnessWeight = zoneState.state === "fresh" ? 1 : zoneState.state === "tested" ? 0.55 : 0.2;
  const impulseWeight = clamp(Number(zoneState.impulseDistance || 0) / 250, 0, 1);

  return freshnessWeight * 0.6 + impulseWeight * 0.4;
}

function getNarrativeStrength(analysis) {
  if (!analysis?.narrative) {
    return 0;
  }

  if (analysis.narrative.state === "CONFIRMED") {
    return 1;
  }

  return analysis.narrative.valid ? 0.55 : 0.2;
}

function getSignalReadiness(signalState) {
  if (signalState?.signal) {
    return {
      readiness: "READY",
      weight: 1,
    };
  }

  if (signalState?.state === "WAIT") {
    return {
      readiness: "MONITORING",
      weight: 0.45,
    };
  }

  return {
    readiness: "FILTERED",
    weight: 0.12,
  };
}

export function rankRadarCandidate({
  symbol,
  timeframe,
  market,
  analysis,
  precheck,
  previousCandidate = null,
  failureCount = 0,
}) {
  const signalReadiness = getSignalReadiness(analysis?.signalState);
  const zoneQuality = getZoneQuality(analysis?.zoneState || analysis?.bosState?.zone);
  const bosConfidence = analysis?.bosState?.state === "LOCKED" ? 1 : 0.35;
  const htfWeight = analysis?.htfAlignment ? 1 : 0.2;
  const structureWeight = analysis?.structure?.event === "BOS" ? 1 : 0.35;
  const narrativeWeight = getNarrativeStrength(analysis);
  const confidence = clamp(
    Math.round(
      precheck.score * 0.25 +
        htfWeight * 20 +
        zoneQuality * 16 +
        bosConfidence * 14 +
        structureWeight * 10 +
        narrativeWeight * 8 +
        signalReadiness.weight * 22 -
        failureCount * 5
    ),
    0,
    100
  );
  const score = clamp(
    Math.round(
      confidence * 0.8 +
        (analysis?.globalContext?.strength || 0) * 14 +
        (analysis?.globalContext?.biasConfidence || 0) * 12
    ),
    0,
    100
  );
  const priority =
    score >= 80 && signalReadiness.readiness === "READY"
      ? "HIGH_PRIORITY"
      : score >= 62
      ? "MEDIUM_PRIORITY"
      : "WATCHLIST";
  const direction =
    analysis?.signalState?.signal?.direction ||
    analysis?.structure?.direction ||
    analysis?.globalContext?.direction ||
    precheck.direction ||
    "NEUTRAL";
  const zoneState = analysis?.zoneState?.state || "unlocked";
  const bosLockState = analysis?.bosState?.state || "WAITING";

  return {
    id: `${symbol}:${timeframe}`,
    symbol,
    timeframe,
    timeframeLabel: formatTimeframeLabel(timeframe),
    market,
    score,
    confidence,
    priority,
    direction,
    readiness: signalReadiness.readiness,
    signalState: analysis?.signalState?.state || "WAIT",
    signalReason:
      analysis?.signalState?.reason ||
      analysis?.result?.signal?.reason ||
      "Monitoring",
    htfAlignment: analysis?.htfAlignment ? "ALIGNED" : "CONFLICT",
    zoneState,
    bosLockState,
    zoneOutcomeState: analysis?.zoneOutcome?.state || "FRESH",
    structureEvent: analysis?.structure?.event || "NONE",
    lastUpdateTime: Date.now(),
    lastUpdateLabel: new Date().toLocaleTimeString(),
    precheckScore: precheck.score,
    biasConfidence: analysis?.globalContext?.biasConfidence || 0,
    htfStrength: analysis?.globalContext?.strength || 0,
    lockedZoneLabel:
      analysis?.bosState?.zone?.label ||
      analysis?.zoneState?.label ||
      analysis?.zoneState?.type ||
      "--",
    pipelineAnalysis: analysis,
    previousScore: previousCandidate?.score || null,
  };
}
